/**
 * Does this ZIP belong to this State? -- the browser-side mirror of
 * api/src/shipping/zip_state_policy.py (Bug Intake #514, Jake 2026-10-23).
 *
 * The cart quotes shipping and sales tax before address verification
 * finishes (2026-10-22), so a ZIP typed against the wrong State (NY with a
 * California ZIP, 91377) used to be rated and taxed as if it were real. This
 * module lets the cart refuse such a pair at the form, with a field error and
 * no quote request.
 *
 * THIS IS A UX HINT, NOT THE GATE. The server applies the same table and
 * refuses the rate, the tax and the order; a client cannot be trusted to
 * enforce it. The table lives in zipStateRanges.json, mirrored byte for byte
 * at api/src/shipping/zip_state_ranges.json (a backend test enforces it).
 *
 * FAILS OPEN. Only a pair that is CLEARLY wrong is a mismatch. Anything this
 * cannot judge -- a non-US country, a ZIP that does not start with three
 * digits, a State it cannot resolve to a code, a ZIP3 that belongs to a
 * territory or to the military (AA/AE/AP, PR, VI, GU), a ZIP3 not in the
 * table -- is NOT a mismatch. A false refusal blocks a real order; a missed
 * one only leaves today's behavior.
 */
import table from "./zipStateRanges.json";

// The 50 states and DC: the only codes a ZIP3 can disagree with.
const STATE_CODES = new Set(
  (
    "AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN " +
    "MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA " +
    "WV WI WY"
  ).split(" ")
);

// Shopper-facing copy, kept verbatim in step with the server's
// ZIP_STATE_MISMATCH_MESSAGE.
export const ZIP_STATE_MISMATCH_MESSAGE =
  "That ZIP code does not belong to the state you chose. " +
  "Please check the ZIP code and the state.";

// "12345", "12345-6789", "123456789", or a prefix still being typed ("123").
const ZIP_SHAPE = /^\s*(\d{3})\d{0,2}(?:[-\s]?\d{0,4})\s*$/;

/** The state codes this ZIP's 3-digit prefix may belong to, or null. */
export function statesForZip(zip) {
  if (typeof zip !== "string") return null;
  const match = ZIP_SHAPE.exec(zip);
  if (!match) return null;
  const prefix = Number(match[1]);
  const shared = table.shared[match[1]];
  const row = table.ranges.find(([lo, hi]) => prefix >= lo && prefix <= hi);
  // An individual ZIP inside another state's prefix (Fishers Island NY under
  // CT's 063): the listed states are accepted too.
  const exception = (table.exceptions || {})[zip.trim().slice(0, 5)];
  if (!row && !shared && !exception) return null;
  return [
    ...new Set([
      ...(row ? [row[2]] : []),
      ...(shared || []),
      ...(exception || []),
    ]),
  ];
}

/**
 * Resolve a State value to a two-letter code: a code in any case, or a full
 * name through `stateNames` ({ "New York": "NY" }, the store's `getStates`).
 * null when it cannot.
 */
export function stateCode(region, stateNames = {}) {
  if (typeof region !== "string") return null;
  const raw = region.trim();
  if (!raw) return null;
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  const byName = Object.entries(stateNames || {}).find(
    ([name]) => name.toLowerCase() === raw.toLowerCase()
  );
  return byName ? String(byName[1]).toUpperCase() : null;
}

/**
 * True only when the ZIP clearly is not in the State. `country` blank is
 * read as US (the form's own default); any other country is not judged.
 */
export function zipStateMismatch(zip, region, { country, stateNames } = {}) {
  if (country && !/^(US|USA|U\.S\.A?\.?|UNITED STATES( OF AMERICA)?)$/i.test(country.trim())) {
    return false;
  }
  const code = stateCode(region, stateNames);
  if (!code || !STATE_CODES.has(code)) return false;
  const allowed = statesForZip(zip);
  if (!allowed) return false;
  // A prefix that belongs to a territory or the military judges nothing.
  if (!allowed.some((s) => STATE_CODES.has(s))) return false;
  return !allowed.includes(code);
}
